import type { MemoryEntry } from "@/types";
import { createId } from "@/utils";
import type { MemoryRepository } from "../MemoryRepository";
import { getDb } from "./connection";

interface MemoryRow {
  id: string;
  content: string;
  tags_json: string;
  created_at: number;
  updated_at: number;
}

function rowToEntry(row: MemoryRow): MemoryEntry {
  return {
    id: row.id,
    content: row.content,
    tags: JSON.parse(row.tags_json) as string[],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const sqliteMemoryRepository: MemoryRepository = {
  async list() {
    const db = await getDb();
    const rows = await db.select<MemoryRow[]>("SELECT * FROM memories ORDER BY updated_at DESC");
    return rows.map(rowToEntry);
  },

  async create(content, tags = []) {
    const db = await getDb();
    const entry: MemoryEntry = {
      id: createId("mem"),
      content,
      tags,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    await db.execute(
      "INSERT INTO memories (id, content, tags_json, created_at, updated_at) VALUES ($1, $2, $3, $4, $5)",
      [entry.id, entry.content, JSON.stringify(entry.tags), entry.createdAt, entry.updatedAt],
    );
    return entry;
  },

  async update(id, patch) {
    const db = await getDb();
    const rows = await db.select<MemoryRow[]>("SELECT * FROM memories WHERE id = $1", [id]);
    const existing = rows[0];
    if (!existing) return;
    const content = patch.content ?? existing.content;
    const tags = patch.tags ?? (JSON.parse(existing.tags_json) as string[]);
    await db.execute("UPDATE memories SET content = $1, tags_json = $2, updated_at = $3 WHERE id = $4", [
      content,
      JSON.stringify(tags),
      Date.now(),
      id,
    ]);
  },

  async delete(id) {
    const db = await getDb();
    await db.execute("DELETE FROM memories WHERE id = $1", [id]);
  },
};
